import React, { useContext, useEffect } from 'react'
import {
  StyledButton,
  StyledButtonsContainer,
  StyledContainer,
  StyledContent,
  StyledContentWrapper,
  StyledDescription,
  StyledHeroHome,
  StyledIFrame,
  StyledImage,
  StyledImageWrapper,
  StyledPlaceholder,
  StyledTitle,
  StyledTitleMarker,
  StyledVideo,
  StyleIcon,
} from './Hero.styled'

import ButtonVariantEnum from '../../enums/ButtonVariant'
import LayoutContext from '../../contexts/LayoutContext'
import Heading from '../Heading/Heading'
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs'
import HeadingSizeEnum from '../../enums/headingSize'
import PropTypes from 'prop-types'
import colorsMatcher from '../../interfaces/ColorsMatcher'
import useCtaLink from '../../hooks/useCtaLink'
import MediaTypeEnum from '../../enums/MediaType'
import { DEFAULT_ALT_IMAGE } from '../../constants/DefaultAlts'
import useMediaQuery from '../../hooks/useMediaQuery'
import { deviceMax } from '../../theme/breakpoints'
import { Helmet } from 'react-helmet'
import { getMediaMetadata } from '../../utils/getMediaMetadata'
import HeroVariantEnum from '../../enums/HeroVariant'

const KALEIDOSCOPE =
  'https://api.kscope.io/ks-ir?key=9e74551c-e101-11ec-853f-0ed8406b0c25&content=stocksm'

const KALEIDOSCOPE_TITLE = 'Results'

const buildMedia = (backgroungColor, media, variant) => {
  // backup backgroundColor
  if (!media) {
    return (
      <StyledPlaceholder
        $backgroundColor={backgroungColor}
        $variant={variant}
      />
    )
  }
  // ContentfulAsset Image
  if (media.contentfulAsset) {
    if (media.contentfulAsset.file.contentType === MediaTypeEnum.mp4) {
      return (
        <StyledVideo
          url={media.contentfulAsset.url}
          withControls={false}
          $variant={variant}
        />
      )
    }
    return (
      <StyledImage
        alt={media.altText?.altText || DEFAULT_ALT_IMAGE}
        image={media.contentfulAsset.gatsbyImageData}
        $variant={variant}
      />
    )
  }
  if (media.cloudinaryAsset) {
    // CloudinaryAsset Video
    if (media.cloudinaryAsset[0]?.resource_type === MediaTypeEnum.video) {
      return (
        <StyledVideo
          url={media.cloudinaryAsset[0].secure_url}
          withControls={false}
          $variant={variant}
        />
      )
    }
    // CloudinaryAsset Image
    if (media.cloudinaryAsset[0].resource_type === MediaTypeEnum.image) {
      return (
        <StyledImage
          alt={
            media.cloudinaryAsset[0].context?.custom.alt ||
            media.altText?.altText ||
            DEFAULT_ALT_IMAGE
          }
          absolutePath={media.cloudinaryAsset[0].secure_url}
          $variant={variant}
        />
      )
    }
  }

  return (
    <StyledPlaceholder $backgroundColor={backgroungColor} $variant={variant} />
  )
}

const Hero = (props) => {
  const {
    backgroundColor,
    className,
    isBreadcrumbsHidden,
    cta,
    ctaOptional,
    introduction,
    mediaDesktop,
    mediaMobile,
    pathname,
    onlyBreadcrumbs = false,
    slug,
    title,
    type,
    variant = HeroVariantEnum.regular,
  } = props

  if (!title) {
    return null
  }
  const isMobile = useMediaQuery(deviceMax.mobileL)

  const bgColor = colorsMatcher[backgroundColor]

  const media = isMobile ? mediaMobile : mediaDesktop
  const { setNavOpacity }= useContext(LayoutContext)

  useEffect(() => {
    setNavOpacity(media)
  }, [media])

  const currentMedia = isMobile
    ? buildMedia(bgColor, mediaMobile, variant)
    : buildMedia(bgColor, mediaDesktop, variant)
  const mediaDatas = isMobile
    ? getMediaMetadata(mediaMobile)
    : getMediaMetadata(mediaDesktop)

  const [ctaUrl, ctaSlug] = cta && cta.url ? useCtaLink(cta.url) : [null, null]
  const [ctaOptionalUrl, ctaOptionalSlug] =
    ctaOptional && ctaOptional.url ? useCtaLink(ctaOptional.url) : [null, null]

  return (
    <StyledHeroHome className={className} $variant={variant}>
      {mediaDatas && mediaDatas.asType === MediaTypeEnum.image && (
        <Helmet>
          <link
            rel="preload"
            as={mediaDatas.asType}
            imageSrcSet={mediaDatas.url}
            href={mediaDatas.url}
          ></link>
        </Helmet>
      )}
      {!isBreadcrumbsHidden && variant !== HeroVariantEnum.home && (
        <Breadcrumbs
          pathname={pathname}
          slug={slug}
          title={title}
          type={type}
        />
      )}
      {!onlyBreadcrumbs &&
      <>
        <StyledContainer $variant={variant}>
          <StyledContentWrapper $variant={variant}>
            <StyledContent>
              <StyledTitle>
                {variant === HeroVariantEnum.home && <StyledTitleMarker />}
                {variant === HeroVariantEnum.home ? (
                  <Heading label={title} size={HeadingSizeEnum.h2} />
                ) : (
                  <Heading label={title} size={HeadingSizeEnum.h1} />
                )}
              </StyledTitle>
              {introduction?.wicIntroduction && (
                <StyledDescription>
                  {introduction.wicIntroduction}
                </StyledDescription>
              )}
              {variant === HeroVariantEnum.home && cta && (
                <StyledButtonsContainer>
                  <StyledButton
                    to={ctaSlug}
                    href={ctaUrl}
                    variant={ButtonVariantEnum.primary}
                    contactEmail={cta?.contactEmail}
                  >
                    {cta.label}
                  </StyledButton>
                  {ctaOptional && (
                    <StyledButton
                      to={ctaOptionalSlug}
                      href={ctaOptionalUrl}
                      variant={ButtonVariantEnum.secondary}
                      target="_blank"
                      contactEmail={ctaOptional?.contactEmail}
                    >
                      {ctaOptional.label}
                      <StyleIcon />
                    </StyledButton>
                  )}
                </StyledButtonsContainer>
              )}
            </StyledContent>
          </StyledContentWrapper>
          {variant === HeroVariantEnum.investor && (
            <StyledIFrame
              src={KALEIDOSCOPE}
              title={KALEIDOSCOPE_TITLE}
            ></StyledIFrame>
          )}
        </StyledContainer>
        <StyledImageWrapper $backgroundColor={backgroundColor} $variant={variant}>
          {currentMedia}
        </StyledImageWrapper>
      </>
      }
    </StyledHeroHome>
  )
}

Hero.propTypes = {
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
  isBreadcrumbsHidden: PropTypes.any,
  cta: PropTypes.object,
  ctaOptional: PropTypes.object,
  introduction: PropTypes.object,
  media: PropTypes.object,
  title: PropTypes.string.isRequired,
  variant: PropTypes.oneOf([HeroVariantEnum.home, HeroVariantEnum.regular]),
}

export default Hero
